import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from '@/router/navigation'
import { normalizeStateCode } from '../components/eligibilitySteps/states'
import { useDocusign } from './selfEnrollmentFlow/hooks/useDocusign'
import { useEnrollmentHeartbeatEffect } from './selfEnrollmentFlow/hooks/useEnrollmentHeartbeatEffect'
import { useSelfEnrollmentEntryEffects } from './selfEnrollmentFlow/hooks/useSelfEnrollmentEntryEffects'
import { useSelfEnrollmentRuntimeState } from './selfEnrollmentFlow/hooks/useSelfEnrollmentRuntimeState'
import { useSelfEnrollmentRouteState } from './selfEnrollmentFlow/hooks/useSelfEnrollmentRouteState'
import { useSessionHydrationEffect } from './selfEnrollmentFlow/hooks/useSessionHydrationEffect'
import { useStepActions } from './selfEnrollmentFlow/hooks/useStepActions'
import { useStepFocusEffect } from './selfEnrollmentFlow/hooks/useStepFocusEffect'
import { useApplyHydration } from './selfEnrollmentFlow/hooks/hydration/useApplyHydration'
import { useEnrollmentDraftSync } from './selfEnrollmentFlow/hooks/hydration/useEnrollmentDraftSync'
import { useEnrollmentSessionHydration } from './selfEnrollmentFlow/hooks/hydration/useEnrollmentSessionHydration'
import { SELF_ENROLLMENT_TRACKED_TOTAL_STEPS } from './selfEnrollmentFlow/utils/steps'
import { getStoredEnrollmentLocationState } from '../utils/enrollmentStorageSelectors'
import { SelfEnrollmentDocusignModal } from './selfEnrollmentFlow/components/SelfEnrollmentDocusignModal'
import { SelfEnrollmentStepRenderer } from './selfEnrollmentFlow/components/SelfEnrollmentStepRenderer'
import {
  StepAccessLoadingState,
  StepValidationErrorState
} from './selfEnrollmentFlow/components/SelfEnrollmentFlowStatus'

export function SelfEnrollmentFlowPage() {
  const navigate = useNavigate()
  const { initialStep, limitToStep } = useSelfEnrollmentRouteState()
  const {
    currentStep,
    fieldEditability,
    setCurrentStep,
    persistedCurrentStep,
    formData,
    updateFormData,
    stepSubmissionError,
    stepValidationError,
    setStepValidationError,
    docusignUrl,
    docusignFrameKey,
    isDocusignModalOpen,
    signatureStatus,
    setSignatureStatus,
    isSignatureSigned,
    setIsSignatureSigned,
    isPreparingDocusign,
    isDocusignIframeLoading,
    isFinalizingDocusign,
    isCheckingSignature,
    isCheckingStepAccess,
    stepContentRef,
    checkingStepRef,
    validationMessageRef,
    suppressNextSessionStepSyncRef,
    sessionStepSyncLockRef,
    storedFormLicenseState,
    storedFormLocationState,
    enrollmentSessionToken,
    setSignatureCompleted,
    resetAnonymousEnrollmentAndReturnToOptions,
    scrollToTop,
    formState,
    sessionState,
    storeBindings,
    stepState,
    navigation,
    stepNavigation,
    stepActionSetters,
    docusignState,
    docusignSetters,
    docusignRefs
  } = useSelfEnrollmentRuntimeState({
    navigate,
    initialStep,
    limitToStep
  })

  const shouldSyncHydratedStep = initialStep === null
  const [isInitialSessionHydrationPending, setIsInitialSessionHydrationPending] = useState(
    () => Boolean(enrollmentSessionToken)
  )

  useEffect(() => {
    if (enrollmentSessionToken) {
      setIsInitialSessionHydrationPending(true)
      return
    }

    setIsInitialSessionHydrationPending(false)
  }, [enrollmentSessionToken])

  const handleInitialHydrationSettled = useCallback(() => {
    setIsInitialSessionHydrationPending(false)
  }, [])

  const { syncStoreFormData } = useEnrollmentDraftSync({
    formData: formState.formData,
    formDataRef: formState.formDataRef,
    updateEnrollmentStoreFormData: storeBindings.updateEnrollmentStoreFormData
  })
  const applyHydration = useApplyHydration({
    formDataRef: formState.formDataRef,
    goToStep: navigation.goToStep,
    setFieldEditability: formState.setFieldEditability,
    setFormData: formState.setFormData,
    sessionState,
    storeBindings,
    syncStoreFormData,
    totalSteps: SELF_ENROLLMENT_TRACKED_TOTAL_STEPS
  })
  const hydrateEnrollmentSession = useEnrollmentSessionHydration({
    applyHydration,
    enrollmentSessionToken: sessionState.enrollmentSessionToken,
    shouldSyncHydratedStep
  })

  const selectedLicenseState = normalizeStateCode(
    formData.driverLicenseState || storedFormLicenseState
  )
  const selectedLocationState = normalizeStateCode(getStoredEnrollmentLocationState(storedFormLocationState))
  const locationStateMismatchWarning =
    selectedLocationState && selectedLicenseState && selectedLocationState !== selectedLicenseState
      ? "Selected training location must be in the same state as your driver's license."
      : null

  const {
    submitEnrollmentStepMutation,
    updateEnrollmentEndorsementsMutation,
    handleEnglishAssessmentPassed,
    handleNext,
    handleBack,
    handleAdditionalInfoContinue,
    handleAdditionalInfoContinueWithoutEndorsements,
    handleEligibilityContinue,
    handleLicenseDetailsContinue,
    handleContactDetailsContinue,
    handleLocationSelectionContinue,
    handleTrainingDateContinue,
    handleDocumentsContinue,
    handleFinalizeEnrollment,
    isFinalizingEnrollment
  } = useStepActions({
    stepState,
    formData,
    sessionState,
    navigation: stepNavigation,
    hydration: { applyHydration },
    setters: stepActionSetters
  })

  const {
    handleOpenDocusignModal,
    handleCloseDocusignModal,
    handleContinueFromDocusign,
    handleDocusignIframeLoad
  } = useDocusign({
    currentStep,
    sessionState,
    docusignState,
    hydration: { hydrateEnrollmentSession },
    navigation,
    actions: {
      finalizeEnrollment: handleFinalizeEnrollment
    },
    setters: docusignSetters,
    refs: docusignRefs
  })

  useSelfEnrollmentEntryEffects({
    navigate,
    initialStep,
    persistedCurrentStep,
    setCurrentStep,
    enrollmentSessionToken
  })

  useSessionHydrationEffect({
    currentStep,
    initialStep,
    enrollmentSessionToken,
    applyHydration,
    shouldSettleInitialHydration: isInitialSessionHydrationPending,
    onInitialHydrationSettled: handleInitialHydrationSettled,
    suppressNextSessionStepSyncRef,
    sessionStepSyncLockRef,
    setSignatureStatus,
    setIsSignatureSigned,
    setSignatureCompleted,
    setStepValidationError
  })

  useStepFocusEffect({
    currentStep,
    isCheckingStepAccess,
    stepValidationError,
    stepContentRef,
    checkingStepRef,
    validationMessageRef
  })

  useEnrollmentHeartbeatEffect({
    currentStep,
    enrollmentSessionToken,
    totalSteps: SELF_ENROLLMENT_TRACKED_TOTAL_STEPS
  })

  if (isCheckingStepAccess || isInitialSessionHydrationPending) {
    return <StepAccessLoadingState containerRef={checkingStepRef} />
  }

  if (stepValidationError) {
    return (
      <StepValidationErrorState
        containerRef={validationMessageRef}
        message={stepValidationError}
        navigate={navigate}
      />
    )
  }

  return (
    <div className="bg-white px-4 py-10">
      <div
        ref={stepContentRef}
        tabIndex={-1}
        className="mx-auto max-w-4xl px-6 pb-20 focus:outline-none md:px-12"
      >
        <SelfEnrollmentStepRenderer
          currentStep={currentStep}
          fieldEditability={fieldEditability}
          formData={formData}
          updateFormData={updateFormData}
          locationStateMismatchWarning={locationStateMismatchWarning}
          isCheckingStepAccess={isCheckingStepAccess}
          enrollmentState={{
            stepSubmissionError,
            submitEnrollmentStepPending: submitEnrollmentStepMutation.isPending,
            updateEnrollmentEndorsementsPending: updateEnrollmentEndorsementsMutation.isPending,
            isFinalizingEnrollment,
            handleBack,
            handleEligibilityContinue,
            handleLicenseDetailsContinue,
            handleContactDetailsContinue,
            handleEnglishAssessmentPassed,
            handleLocationSelectionContinue,
            handleAdditionalInfoContinue,
            handleAdditionalInfoContinueWithoutEndorsements,
            handleTrainingDateContinue,
            handleDocumentsContinue,
            handleFinalizeEnrollment
          }}
          docusignState={{
            isSignatureSigned,
            isPreparingDocusign,
            isCheckingSignature,
            signatureStatus,
            onOpenSigning: handleOpenDocusignModal
          }}
          navigation={{
            scrollToTop,
            onNext: handleNext,
            resetAnonymousEnrollmentAndReturnToOptions,
            setCurrentStep
          }}
        />
      </div>
      <SelfEnrollmentDocusignModal
        isOpen={isDocusignModalOpen}
        isSignatureSigned={isSignatureSigned}
        isFinalizingDocusign={isFinalizingDocusign}
        isCheckingSignature={isCheckingSignature}
        isDocusignIframeLoading={isDocusignIframeLoading}
        signatureStatus={signatureStatus}
        docusignUrl={docusignUrl}
        docusignFrameKey={docusignFrameKey}
        onIframeLoad={handleDocusignIframeLoad}
        onClose={handleCloseDocusignModal}
        onContinue={handleContinueFromDocusign}
      />
    </div>
  )
}
